import { Injectable } from '@angular/core';

declare const gtag: any; // Google Analytics global function
declare const dataLayer: any[];

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService {

    constructor() { }

    /**
     * Generic event tracker
     * @param eventName - Name of the event (e.g., 'page_view_home', 'contact_us_submit')
     * @param params - Optional metadata or parameters
     */
    trackEvent(eventName: string, params: { [key: string]: any } = {}): void {
        try {
            if (typeof gtag !== 'undefined') {
                gtag('event', eventName, params);
            } else if (typeof dataLayer !== 'undefined' && Array.isArray(dataLayer)) {
                dataLayer.push({ event: eventName, ...params });
            } else {
                // GTM/GA not available in this environment; skip silently.
            }
        } catch (error) {
            console.error('Error tracking event:', error);
        }
    }

    /**
     * Page view tracker
     * @param pageName - e.g. 'home', 'about', 'privacy_policy'
     * @param userId - Optional user identifier
     */
    trackPageView(pageName: string, userId?: string): void {
        const currentUrl = window.location.href; // capture current full URL
        this.trackEvent(`page_view_${pageName}`, {
            user_id: userId || 'guest',
            page_url: currentUrl
        });
    }

    /**
     * Track user interactions (clicks, scrolls, etc.)
     * @param action - e.g. 'button_click', 'scroll', 'link_click'
     * @param label - e.g. 'signup_button', 'footer_scroll'
     * @param userId - Optional user identifier
     */
    trackUserInteraction(action: string, label: string, userId?: string): void {
        this.trackEvent(action, { label, user_id: userId || 'guest' });
    }

    /**
     * Track form submissions
     * @param formType - e.g. 'contact_us', 'feedback_form'
     * @param subject - e.g. 'Support Request'
     * @param userId - Optional user identifier
     */
    trackFormSubmit(formType: string, subject: string, userId?: string): void {
        this.trackEvent('form_submit', { form_type: formType, subject, user_id: userId || 'guest' });
    }

    /**
     * Track system or technical errors
     * @param errorMessage - Description of the error
     * @param errorCode - Error code if available
     * @param screenName - Where it occurred
     */
    trackError(errorMessage: string, errorCode: string, screenName: string): void {
        this.trackEvent('error_event', {
            error_message: errorMessage,
            error_code: errorCode,
            screen_name: screenName
        });
    }
}
